import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router, RouterLink } from '@angular/router';
import { defer, finalize, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ShamSlidesSwiper } from 'sham-components/elements/slides-swiper';
import {
  ShamLockPasswordStrokeIcon,
  ShamMail01StrokeIcon,
  ShamPhone03Icon,
} from 'sham-components/icons';
import { AppEnvironment, LanguageService } from 'sham-components/services';
import { SwiperSlide } from 'sham-components/shared/models';
import { AuthApi } from '../../../services/auth-api';
import { ContentApi } from '../../../services/content-api';

@Component({
  selector: 'app-register-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    ShamSlidesSwiper,
    ShamMail01StrokeIcon,
    ShamPhone03Icon,
    ShamLockPasswordStrokeIcon,
    TranslateModule,
  ],
  templateUrl: './register-page.html',
  styleUrl: './register-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterPage {
  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  private readonly phonePattern = /^[0-9+\s()-]{7,20}$/;

  private readonly appEnv = inject(AppEnvironment);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly authApi = inject(AuthApi);
  private readonly contentApi = inject(ContentApi);
  private readonly fb = inject(FormBuilder);
  private readonly lang = inject(LanguageService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  private readonly remoteSlides = signal<any[]>([]);
  isLoading = false;
  errorMessage = '';
  serverError = '';
  submitted = false;

  readonly registerForm = this.fb.nonNullable.group(
    {
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.pattern(this.phonePattern)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
      agreeToTerms: [false, Validators.requiredTrue],
    },
    {
      validators: [RegisterPage.passwordMatchValidator],
    },
  );

  readonly registerSlides = computed<SwiperSlide[]>(() => {
    const slides = this.remoteSlides();
    if (slides.length === 0) return [];

    return slides.map((item: any, index: number) => {
      const imageUrl = item.imageUrl || item.ImageUrl;
      const title = item.title || item.Title;
      const subtitle = item.subtitle || item.Subtitle;

      return {
        id: item.id || item.Id || index,
        order: item.sortOrder || item.SortOrder || index,
        categoryId: '',
        imageUrl: this.getImageUrl(imageUrl),
        categoryLink: '',
        title: title || '',
        bottomText: subtitle || '',
        topText: '',
        subtitle: [],
      };
    });
  });

  constructor() {
    this.registerForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.serverError = '';
    });
    effect(() => {
      const currentLang = this.lang.currentLang();
      this.loadMedia(currentLang);
    });
  }

  private static passwordMatchValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    if (!password || !confirmPassword) {
      return null;
    }

    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }

  get emailControl() {
    return this.registerForm.controls.email;
  }

  get phoneControl() {
    return this.registerForm.controls.phoneNumber;
  }

  get passwordControl() {
    return this.registerForm.controls.password;
  }

  get confirmPasswordControl() {
    return this.registerForm.controls.confirmPassword;
  }

  get agreeToTermsControl() {
    return this.registerForm.controls.agreeToTerms;
  }

  getEmailErrorMessage(): string {
    if (this.emailControl.hasError('emailExists')) {
      return this.t('registerValidation.emailExists');
    }
    if (this.emailControl.hasError('required')) {
      return this.t('registerValidation.emailRequired');
    }

    return this.t('registerValidation.emailInvalid');
  }

  getPhoneErrorMessage(): string {
    if (this.phoneControl.hasError('required')) {
      return this.t('registerValidation.phoneRequired');
    }

    return this.t('registerValidation.phoneInvalid');
  }

  getPasswordErrorMessage(): string {
    if (this.passwordControl.hasError('required')) {
      return this.t('registerValidation.passwordRequired');
    }

    return this.t('registerValidation.passwordMinLength');
  }

  getConfirmPasswordErrorMessage(): string {
    if (this.confirmPasswordControl.hasError('required')) {
      return this.t('registerValidation.confirmPasswordRequired');
    }

    return this.t('registerValidation.passwordMismatch');
  }

  getTermsErrorMessage(): string {
    return this.t('registerValidation.agreeToTermsRequired');
  }

  isEmailValid(): boolean {
    return this.emailPattern.test(this.emailControl.value.trim());
  }

  isPasswordValid(): boolean {
    return this.passwordControl.value.trim().length >= 6;
  }

  hasControlError(
    controlName: 'email' | 'phoneNumber' | 'password' | 'confirmPassword' | 'agreeToTerms',
  ): boolean {
    const control = this.registerForm.controls[controlName];
    return control.invalid && (control.touched || this.submitted);
  }

  hasPasswordMismatch(): boolean {
    return (
      this.registerForm.hasError('passwordMismatch') &&
      (this.confirmPasswordControl.touched ||
        this.passwordControl.touched ||
        this.submitted)
    );
  }

  isConfirmPasswordInvalid(): boolean {
    return this.hasControlError('confirmPassword') || this.hasPasswordMismatch();
  }

  async loadMedia(langCode?: string) {
    try {
      const currentLang = langCode || this.lang.currentLang() || 'en';

      const response = await firstValueFrom(
        this.contentApi.getAuthPage('signup', currentLang),
      );

      const slides = response?.slides || response?.Slides || [];
      this.remoteSlides.set(slides);
    } catch (error) {
      console.error('Error loading register media:', error);
    }
  }

  private getImageUrl(path: string): string {
    if (!path) return 'assets/images/placeholder.jpg';
    if (path.startsWith('http')) return path;
    return `${this.appEnv.BASE_URL()}${path}`;
  }

  private getValidationErrorMessage(): string {
    if (this.emailControl.invalid) {
      return this.getEmailErrorMessage();
    }

    if (this.phoneControl.invalid) {
      return this.getPhoneErrorMessage();
    }

    if (this.passwordControl.invalid) {
      return this.getPasswordErrorMessage();
    }

    if (this.confirmPasswordControl.hasError('required')) {
      return this.getConfirmPasswordErrorMessage();
    }

    if (this.registerForm.hasError('passwordMismatch')) {
      return this.t('registerValidation.passwordMismatch');
    }

    if (this.agreeToTermsControl.invalid) {
      return this.getTermsErrorMessage();
    }

    return this.t('registerValidation.formInvalid');
  }

  onRegister() {
    if (this.isLoading) return;
    this.submitted = true;
    this.errorMessage = '';
    this.serverError = '';
    if (this.emailControl.hasError('emailExists')) {
      this.emailControl.updateValueAndValidity();
    }

    if (!this.emailControl.value.trim()) {
      this.emailControl.markAsTouched();
      this.errorMessage = this.t('registerValidation.emailRequired');
      return;
    }

    if (!this.isEmailValid()) {
      this.emailControl.markAsTouched();
      this.errorMessage = this.t('registerValidation.emailInvalid');
      return;
    }

    if (!this.phoneControl.value.trim()) {
      this.phoneControl.markAsTouched();
      this.errorMessage = this.t('registerValidation.phoneRequired');
      return;
    }

    if (!this.passwordControl.value.trim()) {
      this.passwordControl.markAsTouched();
      this.errorMessage = this.t('registerValidation.passwordRequired');
      return;
    }

    if (!this.isPasswordValid()) {
      this.passwordControl.markAsTouched();
      this.errorMessage = this.t('registerValidation.passwordMinLength');
      return;
    }

    if (!this.confirmPasswordControl.value.trim()) {
      this.confirmPasswordControl.markAsTouched();
      this.errorMessage = this.t('registerValidation.confirmPasswordRequired');
      return;
    }

    if (this.passwordControl.value !== this.confirmPasswordControl.value) {
      this.confirmPasswordControl.markAsTouched();
      this.errorMessage = this.t('registerValidation.passwordMismatch');
      return;
    }

    if (!this.agreeToTermsControl.value) {
      this.agreeToTermsControl.markAsTouched();
      this.errorMessage = this.t('registerValidation.agreeToTermsRequired');
      return;
    }

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      this.errorMessage = this.getValidationErrorMessage();
      return;
    }

    const { email, phoneNumber, password, confirmPassword } =
      this.registerForm.getRawValue();

    this.isLoading = true;

    defer(() =>
      this.authApi.registerCustomer({
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        password,
        confirmPassword,
        fullName: null,
        countryId: null,
        cityId: null,
        address: null,
      }),
    )
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.changeDetector.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.authApi.setAuthFlowData({
            flow: 'register',
            email: email.trim(),
            userId: response.userId,
          });

          this.router.navigate(['/auth/email-verification']);
        },
        error: (err) => {
          if (
            err instanceof HttpErrorResponse &&
            err.error?.error?.code === 'IAM.Auth.EmailExists'
          ) {
            if (this.emailControl.value.trim() === email.trim()) {
              this.emailControl.setErrors({ ...this.emailControl.errors, emailExists: true });
            }
          } else {
            this.serverError = this.t('registerValidation.requestFailed');
          }
        },
      });
  }
}
